import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { guestService } from "@/services/guestService";
import { apiError } from "@/lib/api";
import { GraduationCap, Sparkles, AlertCircle } from "lucide-react";

const MSGS = [
  "Researching your topic...",
  "Designing the curriculum...",
  "Writing the script...",
  "Structuring your chapters...",
  "Crafting your knowledge...",
];

export default function GuestGenerate() {
  const location = useLocation();
  const navigate = useNavigate();
  const topic = location.state?.topic || new URLSearchParams(location.search).get("topic") || "";
  const readingLevel = new URLSearchParams(location.search).get("kids") === "1" ? "kids" : "standard";
  const [progress, setProgress] = useState(8);
  const [msgIdx, setMsgIdx] = useState(0);
  const [error, setError] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (!topic) { navigate("/"); return; }
    if (started.current) return;
    started.current = true;
    guestService.generate(topic, readingLevel)
      .then((d) => { setProgress(100); setTimeout(() => navigate(`/demo/course/${d.course.id}`, { replace: true }), 400); })
      .catch((e) => setError(apiError(e)));
  }, [topic, navigate]);

  useEffect(() => {
    if (error) return;
    const p = setInterval(() => setProgress((v) => (v < 92 ? v + Math.random() * 6 : v)), 700);
    const m = setInterval(() => setMsgIdx((i) => (i + 1) % MSGS.length), 1600);
    return () => { clearInterval(p); clearInterval(m); };
  }, [error]);

  if (error) {
    return (
      <div className="min-h-screen bg-foreground text-background flex flex-col items-center justify-center px-6 text-grain" data-testid="guest-generate-error">
        <AlertCircle className="h-12 w-12 text-primary mb-6" />
        <h2 className="font-display text-3xl font-bold tracking-tight mb-2 text-center">{error}</h2>
        <p className="text-background/70 mb-8 text-center max-w-md">Create a free account to keep generating courses and save them to your library.</p>
        <div className="flex gap-3">
          <button onClick={() => navigate("/register")} className="h-12 px-6 bg-primary text-white font-semibold hover:opacity-90 inline-flex items-center gap-2" data-testid="guest-error-register">
            <GraduationCap className="h-5 w-5" /> Sign up free
          </button>
          <button onClick={() => navigate("/")} className="h-12 px-6 border border-white/20 font-semibold hover:bg-white/10" data-testid="guest-error-home">
            Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-foreground text-background flex flex-col items-center justify-center px-6 text-grain" data-testid="guest-generating-screen">
      <div className="inline-flex items-center gap-2 border border-white/15 px-3 py-1 mb-8 text-xs font-semibold uppercase tracking-[0.2em]">
        <Sparkles className="h-3.5 w-3.5 text-primary" /> Free demo
      </div>
      <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter mb-3 text-center">Crafting your knowledge</h2>
      <p className="text-background/70 mb-10 text-center max-w-md">Building "{topic}" — a 2-minute narrated course, just for you.</p>
      <div className="w-full max-w-md">
        <div className="h-2 bg-white/10 w-full overflow-hidden">
          <div className="h-full bg-primary transition-all duration-700 ease-out" style={{ width: `${progress}%` }} data-testid="guest-progress-bar" />
        </div>
        <p className="mt-4 text-sm text-background/60 text-center">{MSGS[msgIdx]}</p>
      </div>
    </div>
  );
}
